/**
 * Subscription Service
 * 
 * Handles all subscription-related API calls for managing
 * user subscriptions to providers and their subscriber-only content.
 * 
 * Admin endpoints: /v3/...
 * User endpoints: /v2/profile/...
 */

import httpService from "./httpService";
import {
  Subscription,
  SubscriptionWithRelations,
  CreateSubscriptionsRequest,
  CreateSubscriptionsResponse,
  UpdateSubscriptionRequest,
  UpdateSubscriptionResponse,
  DeleteSubscriptionResponse,
  SubscriptionSettings,
  PaginatedSubscriptionsResponse,
  BackfillSubscriptionsResponse,
  CreateInvestorSubscriptionsRequest,
  CreateInvestorSubscriptionsResponse,
} from "../types/subscription";

// ============================================================
// Response Types (internal to service)
// ============================================================

interface ListUserSubscriptionsResponse {
  message: string;
  subscriptions: SubscriptionWithRelations[];
}

interface AdminUserSearchResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
  };
}

// ============================================================
// Subscription Service
// ============================================================

class SubscriptionService {
  private async fetchAllPages(
    fetchPage: (page: number, pageSize: number) => Promise<PaginatedSubscriptionsResponse>
  ): Promise<SubscriptionWithRelations[]> {
    const firstPage = await fetchPage(1, 100);
    const subscriptions = [...firstPage.items];

    if (firstPage.totalPages <= 1) {
      return subscriptions;
    }

    const remainingPages = Array.from(
      { length: firstPage.totalPages - 1 },
      (_, index) => index + 2
    );
    const remainingResults = await Promise.all(
      remainingPages.map((page) => fetchPage(page, 100))
    );

    remainingResults.forEach((result) => {
      subscriptions.push(...result.items);
    });

    return subscriptions;
  }

  // ============================================================
  // Admin: Provider Subscriber Management
  // ============================================================

  /**
   * List subscribers for a provider.
   * GET /v3/providers/{providerId}/subscriptions
   */
  async listProviderSubscriptions(
    providerId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedSubscriptionsResponse> {
    try {
      const response = await httpService.get<PaginatedSubscriptionsResponse>(
        `/v3/providers/${providerId}/subscriptions?page=${page}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      console.error("List provider subscriptions error:", error);
      throw error;
    }
  }

  /**
   * Fetch all subscribers for a provider.
   */
  async listProviderSubscribers(providerId: number): Promise<SubscriptionWithRelations[]> {
    return this.fetchAllPages((page, pageSize) =>
      this.listProviderSubscriptions(providerId, page, pageSize)
    );
  }

  /**
   * Create one or more subscriptions for a provider (batch).
   * POST /v3/providers/{providerId}/subscriptions
   */
  async createSubscriptions(
    providerId: number,
    request: CreateSubscriptionsRequest
  ): Promise<CreateSubscriptionsResponse> {
    try {
      const response = await httpService.post<CreateSubscriptionsResponse>(
        `/v3/providers/${providerId}/subscriptions`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Create subscriptions error:", error);
      throw error;
    }
  }

  /**
   * Create a single subscription (convenience wrapper).
   */
  async createSubscription(
    providerId: number,
    userId: string,
    subscriptionSettings?: SubscriptionSettings
  ): Promise<Subscription | null> {
    const response = await this.createSubscriptions(providerId, {
      subscriptions: [{ userId, subscriptionSettings }],
    });
    const result = response.results[0];
    if (result?.success && result.subscription) {
      return result.subscription;
    }
    if (result?.error) {
      throw new Error(result.error);
    }
    return null;
  }

  // ============================================================
  // Admin: Provider-context Subscription CRUD
  // ============================================================

  /**
   * Update an existing subscription's settings.
   * PUT /v3/providers/{providerId}/subscriptions/{subscriptionId}
   */
  async updateProviderSubscription(
    providerId: number,
    subscriptionId: number,
    request: UpdateSubscriptionRequest
  ): Promise<Subscription> {
    try {
      const response = await httpService.put<UpdateSubscriptionResponse>(
        `/v3/providers/${providerId}/subscriptions/${subscriptionId}`,
        request
      );
      const subscription = response.data.data ?? response.data.subscription;
      if (!subscription) {
        throw new Error("Subscription update response did not include subscription data");
      }
      return subscription;
    } catch (error) {
      console.error("Update subscription error:", error);
      throw error;
    }
  }

  /**
   * Delete a subscription.
   * DELETE /v3/providers/{providerId}/subscriptions/{subscriptionId}
   */
  async deleteProviderSubscription(
    providerId: number,
    subscriptionId: number
  ): Promise<DeleteSubscriptionResponse> {
    try {
      const response = await httpService.delete<DeleteSubscriptionResponse>(
        `/v3/providers/${providerId}/subscriptions/${subscriptionId}`
      );
      return response.data;
    } catch (error) {
      console.error("Delete subscription error:", error);
      throw error;
    }
  }

  /**
   * Backfill external-origin flag for all subscriptions on a provider.
   * POST /v3/providers/{providerId}/subscriptions/backfill
   */
  async backfillProviderSubscriptions(providerId: number): Promise<BackfillSubscriptionsResponse> {
    try {
      const response = await httpService.post<BackfillSubscriptionsResponse>(
        `/v3/providers/${providerId}/subscriptions/backfill`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error("Backfill provider subscriptions error:", error);
      throw error;
    }
  }

  // ============================================================
  // Admin: Investor-context Subscription CRUD
  // ============================================================

  /**
   * List subscriptions for a specific investor.
   * GET /v3/investors/{userId}/subscriptions
   */
  async listAdminInvestorSubscriptions(
    userId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedSubscriptionsResponse> {
    try {
      const encodedUserId = encodeURIComponent(userId);
      const response = await httpService.get<PaginatedSubscriptionsResponse>(
        `/v3/investors/${encodedUserId}/subscriptions?page=${page}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      console.error("List admin investor subscriptions error:", error);
      throw error;
    }
  }

  /**
   * Fetch all subscriptions for a specific investor.
   */
  async listAdminInvestorSubscriptionsAll(userId: string): Promise<SubscriptionWithRelations[]> {
    return this.fetchAllPages((page, pageSize) =>
      this.listAdminInvestorSubscriptions(userId, page, pageSize)
    );
  }

  /**
   * Create one or more subscriptions for a specific investor.
   * POST /v3/investors/{userId}/subscriptions
   */
  async createInvestorSubscriptions(
    userId: string,
    request: CreateInvestorSubscriptionsRequest
  ): Promise<CreateInvestorSubscriptionsResponse> {
    try {
      const encodedUserId = encodeURIComponent(userId);
      const response = await httpService.post<CreateInvestorSubscriptionsResponse>(
        `/v3/investors/${encodedUserId}/subscriptions`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Create investor subscriptions error:", error);
      throw error;
    }
  }

  /**
   * Update a subscription through the investor-context admin endpoint.
   * PUT /v3/investors/{userId}/subscriptions/{subscriptionId}
   */
  async updateInvestorSubscription(
    userId: string,
    subscriptionId: number,
    request: UpdateSubscriptionRequest
  ): Promise<Subscription> {
    try {
      const encodedUserId = encodeURIComponent(userId);
      const response = await httpService.put<UpdateSubscriptionResponse>(
        `/v3/investors/${encodedUserId}/subscriptions/${subscriptionId}`,
        request
      );
      const subscription = response.data.data ?? response.data.subscription;
      if (!subscription) {
        throw new Error("Subscription update response did not include subscription data");
      }
      return subscription;
    } catch (error) {
      console.error("Update investor subscription error:", error);
      throw error;
    }
  }

  /**
   * Delete a subscription through the investor-context admin endpoint.
   * DELETE /v3/investors/{userId}/subscriptions/{subscriptionId}
   */
  async deleteInvestorSubscription(
    userId: string,
    subscriptionId: number
  ): Promise<DeleteSubscriptionResponse> {
    try {
      const encodedUserId = encodeURIComponent(userId);
      const response = await httpService.delete<DeleteSubscriptionResponse>(
        `/v3/investors/${encodedUserId}/subscriptions/${subscriptionId}`
      );
      return response.data;
    } catch (error) {
      console.error("Delete investor subscription error:", error);
      throw error;
    }
  }

  // Legacy v2 wrappers retained for older admin call sites.
  async updateSubscription(
    subscriptionId: number,
    request: UpdateSubscriptionRequest
  ): Promise<Subscription> {
    try {
      const response = await httpService.put<UpdateSubscriptionResponse>(
        `/v2/admin/subscriptions/${subscriptionId}`,
        request
      );
      const subscription = response.data.subscription ?? response.data.data;
      if (!subscription) {
        throw new Error("Subscription update response did not include subscription data");
      }
      return subscription;
    } catch (error) {
      console.error("Update legacy subscription error:", error);
      throw error;
    }
  }

  async deleteSubscription(subscriptionId: number): Promise<void> {
    try {
      await httpService.delete<DeleteSubscriptionResponse>(
        `/v2/admin/subscriptions/${subscriptionId}`
      );
    } catch (error) {
      console.error("Delete legacy subscription error:", error);
      throw error;
    }
  }

  // ============================================================
  // Admin: User Lookup
  // ============================================================

  /**
   * Search for a user by email (admin only).
   * GET /v2/admin/users/email/{email}
   * 
   * Note: Returns 404 if user not found.
   */
  async searchUserByEmail(email: string): Promise<{ id: string; email: string; name: string | null } | null> {
    try {
      const encodedEmail = encodeURIComponent(email);
      const response = await httpService.get<AdminUserSearchResponse>(
        `/v2/admin/users/email/${encodedEmail}`
      );
      return response.data.user;
    } catch (error: unknown) {
      // Handle 404 - user not found
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      console.error("Search user by email error:", error);
      throw error;
    }
  }

  // ============================================================
  // User: My Subscriptions
  // ============================================================

  /**
   * Get the authenticated user's subscriptions.
   * GET /v2/profile/subscriptions
   */
  async getMySubscriptions(): Promise<SubscriptionWithRelations[]> {
    try {
      const response = await httpService.get<ListUserSubscriptionsResponse>(
        "/v2/profile/subscriptions"
      );
      return response.data.subscriptions;
    } catch (error) {
      console.error("Get my subscriptions error:", error);
      throw error;
    }
  }

  /**
   * List subscriptions for the authenticated investor.
   * GET /v3/investor/subscriptions
   */
  async listInvestorSubscriptions(
    page: number = 1,
    pageSize: number = 100
  ): Promise<PaginatedSubscriptionsResponse> {
    try {
      const response = await httpService.get<PaginatedSubscriptionsResponse>(
        `/v3/investor/subscriptions?page=${page}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      console.error("List investor subscriptions error:", error);
      throw error;
    }
  }

  /**
   * Fetch all subscriptions for the authenticated investor.
   */
  async getInvestorSubscriptions(): Promise<SubscriptionWithRelations[]> {
    return this.fetchAllPages((page, pageSize) =>
      this.listInvestorSubscriptions(page, pageSize)
    );
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
